
import { useState, useEffect, useCallback } from "react";
import { SetData } from "../../../types";

interface UsePersistedSetDataProps {
  exerciseId: string;
  sets: number;
  defaultReps: number;
  defaultWeight: number;
}

interface PersistedSetData extends SetData {
  lastUpdated: number;
  userConfirmed: boolean;
}

export const usePersistedSetData = ({
  exerciseId,
  sets,
  defaultReps,
  defaultWeight
}: UsePersistedSetDataProps) => {
  const storageKey = `clubbers_set_data_${exerciseId}`;
  
  // Cargar datos del localStorage
  const loadPersistedData = useCallback((): PersistedSetData[] => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        console.log(`📦 Loaded persisted data for exercise ${exerciseId}:`, parsed);
        return parsed;
      }
    } catch (error) {
      console.warn(`⚠️ Error loading persisted data for exercise ${exerciseId}:`, error);
    }
    return [];
  }, [storageKey, exerciseId]);

  // Guardar datos en localStorage
  const savePersistedData = useCallback((data: PersistedSetData[]) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(data));
      console.log(`💾 Saved persisted data for exercise ${exerciseId}:`, data);
    } catch (error) {
      console.warn(`⚠️ Error saving persisted data for exercise ${exerciseId}:`, error);
    }
  }, [storageKey, exerciseId]);

  // Obtener datos de serie específica
  const getSetData = useCallback((setIndex: number): SetData & { isSaved: boolean } => {
    const persistedData = loadPersistedData();
    const setData = persistedData[setIndex];
    
    if (setData && setData.userConfirmed) {
      return {
        reps: setData.reps,
        weight: setData.weight,
        isSaved: true
      };
    }
    
    return {
      reps: defaultReps,
      weight: defaultWeight,
      isSaved: false
    };
  }, [loadPersistedData, defaultReps, defaultWeight]);

  // Guardar datos de serie específica
  const saveSetData = useCallback((setIndex: number, reps: number, weight: number) => {
    const persistedData = loadPersistedData();
    
    // Asegurar que el array tenga el tamaño correcto
    while (persistedData.length <= setIndex) {
      persistedData.push({
        reps: defaultReps,
        weight: defaultWeight,
        lastUpdated: Date.now(),
        userConfirmed: false
      });
    }
    
    // Actualizar la serie específica
    persistedData[setIndex] = {
      reps,
      weight,
      lastUpdated: Date.now(),
      userConfirmed: true
    };
    
    savePersistedData(persistedData);
    console.log(`✅ Set ${setIndex} saved:`, { reps, weight });
  }, [loadPersistedData, savePersistedData, defaultReps, defaultWeight]);

  // Obtener todos los datos de las series
  const getAllSetData = useCallback((): (SetData & { isSaved: boolean })[] => {
    return Array.from({ length: sets }, (_, index) => getSetData(index));
  }, [sets, getSetData]);

  // Verificar si una serie tiene datos guardados
  const isSetSaved = useCallback((setIndex: number): boolean => {
    const persistedData = loadPersistedData();
    return persistedData[setIndex]?.userConfirmed || false;
  }, [loadPersistedData]);

  // Limpiar datos de un ejercicio específico
  const clearPersistedData = useCallback(() => {
    localStorage.removeItem(storageKey);
    console.log(`🗑️ Cleared persisted data for exercise ${exerciseId}`);
  }, [storageKey, exerciseId]);

  return {
    getSetData,
    saveSetData,
    getAllSetData,
    isSetSaved,
    clearPersistedData
  };
};
